import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { getTags } from '../services/gig.service';

const Keywords = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const dropdownRef = useRef(null);

  // Fetch all tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const data = await getTags();
        if (data.success) {
          const options = data.tags.map(tag => ({ id: tag.name, label: tag.name }));
          setKeywordOptions(options);
        }
      } catch (err) {
        console.error("Failed to fetch tags:", err);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const keywordsParam = urlParams.get('keywords');
    if (keywordsParam) {
      setSelectedKeywords(keywordsParam.split(','));
    } else {
      setSelectedKeywords([]);
    }
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const toggleKeyword = (keywordId) => {
    setSelectedKeywords(prev => {
      if (prev.includes(keywordId)) {
        return prev.filter(id => id !== keywordId);
      } else {
        return [...prev, keywordId];
      }
    });
  };

  const clearAll = () => {
    setSelectedKeywords([]);
    const url = new URL(window.location);
    url.searchParams.delete('keywords');
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };

  const applyFilters = () => {
    const url = new URL(window.location);
    if (selectedKeywords.length > 0) {
      url.searchParams.set('keywords', selectedKeywords.join(','));
    } else {
      url.searchParams.delete('keywords');
    }
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block w-[100px] md:w-[150px]" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-between w-full px-2 md:px-4 py-1 md:py-2 text-[12px] md:text-sm font-medium text-gray-700 bg-white border border-[#185BC3] rounded-[50px] shadow-sm hover:bg-gray-50 focus:outline-none"
      >
        <span className='text-[#185BC3]'>
          Keywords {selectedKeywords.length > 0 && `(${selectedKeywords.length})`}
        </span>
        <ChevronDown className={`w-4 h-4 ml-0 transition-transform text-[#185BC3] font-extrabold duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="p-3 absolute z-10 w-64 mt-2 origin-top-right bg-white border border-[#9D9D9D40] rounded-md shadow-[5px]">
          <div className="flex flex-col gap-2">
            <p className='text-black font-medium font-poppins text-[16px]'>Filter by Keywords</p>

            <div className="max-h-48 overflow-y-auto">
              {keywordOptions.map((keyword) => (
                <label key={keyword.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1">
                  <input
                    type="checkbox"
                    checked={selectedKeywords.includes(keyword.id)}
                    onChange={() => toggleKeyword(keyword.id)}
                    className="w-4 h-4 text-[#185BC3] border-gray-300 rounded focus:ring-[#185BC3] focus:ring-2"
                  />
                  <span className="text-sm text-gray-700">{keyword.label}</span>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200">
              <button
                onClick={clearAll}
                className='border-[1px] border-[#174568] text-[#174568] text-[15px] rounded-[30px] py-[.5px] px-3 hover:bg-gray-50'
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className='border-[1px] bg-[#174568] border-[#174568] text-[15px] text-white rounded-[30px] py-[.5px] px-3 hover:bg-[#0f3a4f]'
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Keywords;